import { useMemo, useRef, useState } from "react";
import { navigateHook } from "../../../../hooks/use-navigate";
import ProjectUtils from "../../../../utils/project-utils"
import { Template } from "../../../../components/template/template";
import { useAutoAssignListQuery } from "../components/use-assign-list-query";
import Button from "../../../../components/button/button";
import { FaEraser, FaPlus, FaSearch } from "react-icons/fa";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils";

export default function AutoAssignList() {
    const navigate = navigateHook();

    const [plate, setPlate] = useState("");
    const [plateInput, setPlateInput] = useState("");

    const { data, isLoading } = useAutoAssignListQuery()

    const rows = data?.rows ?? [];

    const columnDefs = useMemo(() => {
        const plateComparator = (valueA, valueB) => {
            const a = (valueA || "").toString().toUpperCase();
            const b = (valueB || "").toString().toUpperCase();

            return a.localeCompare(b, "pt-BR");
        };
        return [
            {
                headerName: "Placa",
                field: "plate",
                sortable: true,
                comparator: plateComparator,
                flex: 1,
                cellClass: "uppercase font-bold",
            },
            {
                headerName: "Usuário",
                field: "userName",
                sortable: true,
                flex: 1,
            },
            {
                headerName: "Data criada",
                field: "createdAt",
                sortable: true,
                sort: "desc",
                flex: 1,
                valueFormatter: (params) => 
                    params.value
                        ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY HH:mm")
                        : "",
            },
            {
                headerName: "Expira em",
                field: "expiresAt",
                sortable: true,
                flex: 1,
                valueFormatter: (params) => 
                    params.value
                        ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY HH:mm")
                        : "",
            },
            {
                headerName: "Último evento",
                field: "alarmDate",
                sortable: true,
                flex: 1,
                valueFormatter: (params) => 
                    params.value
                        ? ProjectUtils.formatDate(params.value, "DD/MM/YYYY HH:mm")
                        : "",
            },
        ];
    }, []);

    const gridRef = useRef(null);
    const goFirstPage = () => gridRef.current?.api?.paginationGoToFirstPage();

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
        if (event.key === "Escape") {
            handleClear();
        }
    };

    const handleSearch = () => {
        setPlate(plateInput.trim());
        goFirstPage();
    };

    const handleClear = () => {
        setPlate("");
        setPlateInput("");
        goFirstPage();
    };

    const onCellClicked = ({ data, event }) => {
        if (!data?.id) return;

        const urlDev = `/auto-assign/form/${data.id}`;
        let isBlank = !!event.ctrlKey;

        navigate(urlDev, isBlank);
    };

    const filteredRows = useMemo(() => {
        const plateFilter = (plate || "").trim().toLowerCase();
        if (!plateFilter) return rows;

        return rows.filter((row) => (row.plate ?? "").toLowerCase().includes(plateFilter));
    }, [rows, plate]);

    const newPlate = () => {
        const urlDev = `/auto-assign/form`;
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Assinatura automática de alarmes e eventos de integrações</Template.Title>
                <Button onClick={newPlate}><FaPlus />Adicionar placa</Button>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <div className="flex-1">
                                <InputForm 
                                    id="plate"
                                    label="Placa"
                                    value={plateInput}
                                    onChange={(e) => setPlateInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button 
                                    title="buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-153px)] w-full">
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredRows}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                suppressCellFocus={true}
                                animateRows={false}
                                onCellClicked={onCellClicked}
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}